import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  useMediaQuery,
  TextField,
  Button,
  IconButton,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  InputAdornment,
  Avatar,
  Tooltip,
  Card,
  CardContent,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  alpha,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  TableSortLabel,
  Divider,
  Grow,
  Zoom,
} from '@mui/material';

import {
  Search,
  Visibility,
  CheckCircle,
  Pending,
  Download,
  Refresh,
  Clear,
  Assignment,
  CalendarToday,
  Verified,
  Person,
  ThumbUp,
  LocalShipping,
  Receipt,
  AssignmentTurnedIn,
  Warning,
  HourglassEmpty,
  DeleteOutline,
  Block
} from '@mui/icons-material';

import { styled } from '@mui/material/styles';
import * as XLSX from 'xlsx';
import Sidebar from '../../../../components/sidebars/sidebar_hod';
import TopNav from '../../../../components/TopNav_hotelStaff';

import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const StyledCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.light, 0.05)} 100%)`,
  borderRadius: '20px',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  transition: 'all 0.3s ease-in-out',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
    borderColor: alpha(theme.palette.primary.main, 0.3),
  }
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: '16px',
  background: theme.palette.background.paper,
  boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.05)}`,
  overflowX: 'auto',
  '& .MuiTable-root': {
    minWidth: 1000,
  },
  '& .MuiTableHead-root .MuiTableCell-root': {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
    fontWeight: 700,
    fontSize: '0.85rem',
    borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
    position: 'sticky',
    top: 0,
    zIndex: 10,
    whiteSpace: 'nowrap',
  },
  '& .MuiTableBody-root .MuiTableCell-root': {
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
    whiteSpace: 'nowrap',
  },
  '& .MuiTableRow-root:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.02),
  }
}));

const StatusChip = styled(Chip)(({ theme, status }) => {
  const statusString = String(status || 'Pending').toLowerCase();
  
  let config = {};
  
  if (statusString === 'rejected') {
    config = {
      backgroundColor: alpha(theme.palette.error.main, 0.12),
      color: theme.palette.error.main,
      border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
    };
  } else if (statusString === 'approved by gm') {
    config = {
      backgroundColor: alpha(theme.palette.success.main, 0.12),
      color: theme.palette.success.main,
      border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
    };
  } else if (statusString === 'approved by dept head') {
    config = {
      backgroundColor: alpha(theme.palette.info.main, 0.12),
      color: theme.palette.info.main,
      border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
    };
  } else if (statusString === 'issued') {
    config = {
      backgroundColor: alpha(theme.palette.primary.main, 0.12),
      color: theme.palette.primary.main,
      border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
    };
  } else if (statusString === 'received') {
    config = {
      backgroundColor: alpha(theme.palette.success.main, 0.12),
      color: theme.palette.success.main,
      border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
    };
  } else {
    config = {
      backgroundColor: alpha(theme.palette.warning.main, 0.12),
      color: theme.palette.warning.main,
      border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
    };
  }
  
  return {
    borderRadius: '20px',
    fontWeight: 600,
    fontSize: '0.75rem',
    height: '28px',
    ...config
  };
});

const HODRequisitionApproval = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [loggedInUser, setLoggedInUser] = useState('');
  const [userRole, setUserRole] = useState('');
  const [userDepartment, setUserDepartment] = useState('');
  
  // Search and filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState('all');
  const [orderBy, setOrderBy] = useState('request_date');
  const [order, setOrder] = useState('desc');
  
  // Data states
  const [requisitions, setRequisitions] = useState([]);
  const [totalRequisitions, setTotalRequisitions] = useState(0);
  const [statistics, setStatistics] = useState({
    total: 0,
    pending_dept: 0,
    pending_gm: 0,
    approved: 0,
    issued: 0,
    completed: 0,
    rejected: 0
  });
  const [loading, setLoading] = useState(false);
  
  // Dialog states
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedRequisition, setSelectedRequisition] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [approvalNotes, setApprovalNotes] = useState('');
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  // Fetch requisitions for HOD's department
  const fetchRequisitions = useCallback(async () => {
    if (!userDepartment || userDepartment === 'Not Assigned') {
      return;
    }
    
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('department', userDepartment);
      if (searchQuery) params.append('search', searchQuery);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      params.append('limit', rowsPerPage);
      params.append('offset', page * rowsPerPage);

      const response = await axios.get(`${API_BASE_URL}/hotel/inventory/get_department_requisitions.php?${params}`);
      
      if (response.data.success) {
        setRequisitions(response.data.data);
        setTotalRequisitions(response.data.total);
        setStatistics(response.data.statistics);
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error('Error fetching requisitions:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to fetch requisitions',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [userDepartment, searchQuery, statusFilter, page, rowsPerPage]);

  useEffect(() => {
    if (userDepartment && userDepartment !== 'Not Assigned') {
      fetchRequisitions();
    }
  }, [fetchRequisitions, userDepartment]);

  useEffect(() => {
    setPage(0);
  }, [searchQuery, statusFilter]);

  useEffect(() => {
    const userName = localStorage.getItem('userName');
    const userEmail = localStorage.getItem('email');
    const role = localStorage.getItem('role');
    const department = localStorage.getItem('userDepartment');
    
    setLoggedInUser(userName || userEmail || 'System User');
    setUserRole(role || 'staff');
    setUserDepartment(department || '');
  }, []);

  // Check if user is authorized as HOD
  const isAuthorizedHOD = () => {
    return userRole === 'dept_head' || userRole === 'admin' || userRole === 'hod';
  };

  // Approve requisition
  const handleApproveRequisition = async () => {
    if (!selectedRequisition) return;
    
    setLoading(true);
    try {
      const payload = {
        id: selectedRequisition.id,
        hod_name: loggedInUser,
        department: userDepartment,
        notes: approvalNotes
      };

      const response = await axios.post(`${API_BASE_URL}/hotel/inventory/approve_requisition.php`, payload);
      
      if (response.data.success) {
        setSnackbar({
          open: true,
          message: `Requisition #${selectedRequisition.id} approved successfully`,
          severity: 'success'
        });
        handleCloseApproveDialog();
        fetchRequisitions();
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error('Error approving requisition:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to approve requisition',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Reject/Delete requisition
  const handleRejectRequisition = async () => {
    if (!selectedRequisition) return;
    
    setLoading(true);
    try {
      const payload = {
        id: selectedRequisition.id,
        hod_name: loggedInUser,
        department: userDepartment,
        rejection_reason: rejectionReason
      };

      const response = await axios({
        method: 'delete',
        url: `${API_BASE_URL}/hotel/inventory/reject_requisition.php`,
        data: payload,
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (response.data.success) {
        setSnackbar({
          open: true,
          message: `Requisition #${selectedRequisition.id} rejected`,
          severity: 'success'
        });
        handleCloseRejectDialog();
        fetchRequisitions();
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error('Error rejecting requisition:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to reject requisition',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Export to Excel
  const handleExportToExcel = () => {
    const exportData = requisitions.map(item => ({
      'ID': item.id,
      'Item Name': item.item_name,
      'Quantity': item.quantity_requested,
      'Unit': item.unit_of_measurement || 'N/A',
      'Requested By': item.requested_by,
      'Request Date': item.request_date,
      'Dept Head Approval': item.approved_by_dept_head || 'Pending',
      'GM Approval': item.approved_by_GM || 'Pending',
      'Status': item.status,
      'Notes': item.notes || 'N/A'
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, `${userDepartment}_Requisitions`);
    XLSX.writeFile(wb, `${userDepartment}_requisitions_${new Date().toISOString().split('T')[0]}.xlsx`);
    
    setSnackbar({
      open: true,
      message: 'Export successful!',
      severity: 'success'
    });
  };

  const handleOpenViewDialog = (requisition) => {
    setSelectedRequisition(requisition);
    setViewDialogOpen(true);
  };

  const handleOpenApproveDialog = (requisition) => {
    setSelectedRequisition(requisition);
    setApprovalNotes('');
    setApproveDialogOpen(true);
  };

  const handleOpenRejectDialog = (requisition) => {
    setSelectedRequisition(requisition);
    setRejectionReason('');
    setRejectDialogOpen(true);
  };

  const handleCloseViewDialog = () => setViewDialogOpen(false);
  const handleCloseApproveDialog = () => setApproveDialogOpen(false);
  const handleCloseRejectDialog = () => setRejectDialogOpen(false);

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortData = (data) => {
    if (!orderBy) return data;
    return [...data].sort((a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];
      
      if (orderBy === 'quantity_requested') {
        aValue = parseFloat(aValue);
        bValue = parseFloat(bValue);
      }
      
      if (orderBy === 'request_date' || orderBy === 'created_at') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }
      
      if (orderBy === 'available_stock') {
        aValue = parseFloat(aValue);
        bValue = parseFloat(bValue);
      }
      
      if (aValue < bValue) return order === 'asc' ? -1 : 1;
      if (aValue > bValue) return order === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setPage(0);
  };

  // Check if requisition is pending HOD approval
  const isPendingHODApproval = (requisition) => {
    return (!requisition.approved_by_dept_head || requisition.approved_by_dept_head === '') && 
           requisition.status !== 'Rejected';
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch(status) {
      case 'Rejected': return <Block fontSize="small" />;
      case 'Approved by GM': return <Verified fontSize="small" />;
      case 'Approved by Dept Head': return <ThumbUp fontSize="small" />;
      case 'Issued': return <LocalShipping fontSize="small" />;
      case 'Received': return <CheckCircle fontSize="small" />;
      default: return <Pending fontSize="small" />;
    }
  };

  // Render statistics cards
  const renderStatisticsCards = () => (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      <Grid item xs={12} sm={6} md={2.4}>
        <Zoom in={true} style={{ transitionDelay: '0ms' }}>
          <StyledCard onClick={() => setStatusFilter('pending_dept')}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={500}>
                    Pending HOD
                  </Typography>
                  <Typography variant="h3" fontWeight={700} mt={1} color="warning.main">
                    {statistics.pending_dept}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1), color: theme.palette.warning.main, width: 56, height: 56 }}>
                  <HourglassEmpty sx={{ fontSize: 32 }} />
                </Avatar>
              </Box>
            </CardContent>
          </StyledCard>
        </Zoom>
      </Grid>
      
      <Grid item xs={12} sm={6} md={2.4}>
        <Zoom in={true} style={{ transitionDelay: '100ms' }}>
          <StyledCard onClick={() => setStatusFilter('pending_gm')}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={500}>
                    Pending GM
                  </Typography>
                  <Typography variant="h3" fontWeight={700} mt={1} color="info.main">
                    {statistics.pending_gm}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), color: theme.palette.info.main, width: 56, height: 56 }}>
                  <Verified sx={{ fontSize: 32 }} />
                </Avatar>
              </Box>
            </CardContent>
          </StyledCard>
        </Zoom>
      </Grid>
      
      <Grid item xs={12} sm={6} md={2.4}>
        <Zoom in={true} style={{ transitionDelay: '200ms' }}>
          <StyledCard onClick={() => setStatusFilter('approved')}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={500}>
                    Approved
                  </Typography>
                  <Typography variant="h3" fontWeight={700} mt={1} color="success.main">
                    {statistics.approved}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), color: theme.palette.success.main, width: 56, height: 56 }}>
                  <ThumbUp sx={{ fontSize: 32 }} />
                </Avatar>
              </Box>
            </CardContent>
          </StyledCard>
        </Zoom>
      </Grid>

      <Grid item xs={12} sm={6} md={2.4}>
        <Zoom in={true} style={{ transitionDelay: '300ms' }}>
          <StyledCard onClick={() => setStatusFilter('issued')}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={500}>
                    Issued
                  </Typography>
                  <Typography variant="h3" fontWeight={700} mt={1} color="primary.main">
                    {statistics.issued}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main, width: 56, height: 56 }}>
                  <LocalShipping sx={{ fontSize: 32 }} />
                </Avatar>
              </Box>
            </CardContent>
          </StyledCard>
        </Zoom>
      </Grid>

      <Grid item xs={12} sm={6} md={2.4}>
        <Zoom in={true} style={{ transitionDelay: '400ms' }}>
          <StyledCard onClick={() => setStatusFilter('rejected')}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={500}>
                    Rejected
                  </Typography>
                  <Typography variant="h3" fontWeight={700} mt={1} color="error.main">
                    {statistics.rejected || 0}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: alpha(theme.palette.error.main, 0.1), color: theme.palette.error.main, width: 56, height: 56 }}>
                  <Block sx={{ fontSize: 32 }} />
                </Avatar>
              </Box>
            </CardContent>
          </StyledCard>
        </Zoom>
      </Grid>
    </Grid>
  );

  // Render requisitions table
  const renderRequisitionsTable = () => (
    <StyledTableContainer>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>ID</TableCell>
            <TableCell>Requested By</TableCell>
            <TableCell>Item Name</TableCell>
            <TableCell align="right">Qty</TableCell>
            <TableCell>Unit</TableCell>
            <TableCell align="right">Available Stock</TableCell>
            <TableCell>Request Date</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="center">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                <CircularProgress />
              </TableCell>
            </TableRow>
          ) : sortData(requisitions).length === 0 ? (
            <TableRow>
              <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                <Typography variant="body1" color="text.secondary">
                  No requisitions found for {userDepartment} department
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            sortData(requisitions).map((item) => (
              <TableRow key={item.id} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>#{item.id}</Typography>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ width: 28, height: 28, bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                      <Person sx={{ fontSize: 16, color: theme.palette.primary.main }} />
                    </Avatar>
                    <Typography variant="body2">{item.requested_by}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ width: 28, height: 28, bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                      <Assignment sx={{ fontSize: 16, color: theme.palette.primary.main }} />
                    </Avatar>
                    <Typography variant="body2" fontWeight={600}>{item.item_name}</Typography>
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={500}>
                    {Number(item.quantity_requested).toLocaleString()}
                  </Typography>
                </TableCell>
                <TableCell>{item.unit_of_measurement || 'N/A'}</TableCell>
                <TableCell align="right">
                  <Chip 
                    size="small"
                    label={`${Number(item.available_stock || 0).toLocaleString()} ${item.unit_of_measurement || ''}`}
                    color={(item.available_stock || 0) < item.quantity_requested ? 'error' : 'default'}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <CalendarToday sx={{ fontSize: 12, color: 'text.secondary' }} />
                    <Typography variant="caption">{item.request_date}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <StatusChip 
                    label={item.status} 
                    status={item.status} 
                    size="small"
                    icon={getStatusIcon(item.status)}
                  />
                </TableCell>
                <TableCell align="center">
                  <Stack direction="row" spacing={0.5} justifyContent="center">
                    <Tooltip title="View Details">
                      <IconButton size="small" onClick={() => handleOpenViewDialog(item)}>
                        <Visibility fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    
                    {isPendingHODApproval(item) && (
                      <>
                        <Tooltip title="Approve Requisition">
                          <IconButton 
                            size="small" 
                            color="success"
                            onClick={() => handleOpenApproveDialog(item)}
                          >
                            <ThumbUp fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="Reject">
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => handleOpenRejectDialog(item)}
                          >
                            <DeleteOutline fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50, 100]}
        component="div"
        count={totalRequisitions}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
      />
    </StyledTableContainer>
  );

 


  return (
    <Box sx={{ display: 'flex', backgroundColor: theme.palette.background.default, minHeight: '100vh' }}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      
      <Box sx={{
        flexGrow: 1,
        p: 3,
        ml: isMobile ? 0 : sidebarOpen ? 30 : 5,
        transition: 'margin 0.3s ease-in-out',
        maxWidth: '1600px',
        mx: 'auto',
        width: '100%'
      }}>
        <TopNav
          onLogout={() => {
            localStorage.removeItem('authToken');
            localStorage.removeItem('userProfile');
            window.location.href = '/login';
          }}
          onNotificationClick={() => console.log('Notifications')}
          toggleSidebar={toggleSidebar}
        />

        <Box sx={{ mt: 3 }}>
          {/* Header */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h4" fontWeight={700} gutterBottom>
              <ThumbUp sx={{ mr: 2, verticalAlign: 'middle', fontSize: 32, color: theme.palette.primary.main }} />
              HOD Requisition Approval
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Review and approve requisitions from your department: <strong>{userDepartment}</strong>
            </Typography>
          </Box>

          {/* Statistics Cards */}
          {renderStatisticsCards()}

          {/* Main Content */}
          <Paper elevation={0} sx={{ borderRadius: '20px', overflow: 'hidden' }}>
            {/* Header Bar */}
            <Box sx={{ 
              p: 3, 
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.light, 0.02)} 100%)`,
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
            }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                <Box display="flex" alignItems="center" gap={2}>
                  <Receipt sx={{ fontSize: 28, color: theme.palette.primary.main }} />
                  <Box>
                    <Typography variant="h6" fontWeight={600}>
                      Department Requisitions
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {userDepartment} Department
                    </Typography>
                  </Box>
                </Box>
                
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    startIcon={<Download />}
                    onClick={handleExportToExcel}
                    size="small"
                  >
                    Export
                  </Button>
                  <IconButton onClick={() => fetchRequisitions()} size="small">
                    <Refresh />
                  </IconButton>
                </Stack>
              </Box>
            </Box>

            {/* Search and Filters */}
            <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    placeholder="Search by item name, requester, or notes..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search color="action" fontSize="small" />
                        </InputAdornment>
                      ),
                      endAdornment: searchQuery && (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setSearchQuery('')}>
                            <Clear fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap">
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                      <InputLabel>Status Filter</InputLabel>
                      <Select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        label="Status Filter"
                      >
                        <MenuItem value="all">All Status</MenuItem>
                        <MenuItem value="pending_dept">Pending HOD Approval</MenuItem>
                        <MenuItem value="pending_gm">Pending GM Approval</MenuItem>
                        <MenuItem value="approved">Approved</MenuItem>
                        <MenuItem value="issued">Issued</MenuItem>
                        <MenuItem value="completed">Completed</MenuItem>
                        <MenuItem value="rejected">Rejected</MenuItem>
                      </Select>
                    </FormControl>
                    
                    {(searchQuery || statusFilter !== 'all') && (
                      <Button size="small" onClick={clearFilters} startIcon={<Clear />}>
                        Clear
                      </Button>
                    )}
                  </Stack>
                </Grid>
              </Grid>
            </Box>

            {/* Active Filters Display */}
            {(searchQuery || statusFilter !== 'all') && (
              <Box sx={{ p: 2, display: 'flex', gap: 1, flexWrap: 'wrap', borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>Active Filters:</Typography>
                {searchQuery && (
                  <Chip label={`Search: ${searchQuery}`} onDelete={() => setSearchQuery('')} size="small" />
                )}
                {statusFilter !== 'all' && (
                  <Chip label={`Status: ${statusFilter.replace('_', ' ')}`} onDelete={() => setStatusFilter('all')} size="small" />
                )}
              </Box>
            )}

            {/* Table */}
            {renderRequisitionsTable()}
          </Paper>
        </Box>
      </Box>

      {/* View Details Dialog - Keep as is */}
      <Dialog open={viewDialogOpen} onClose={handleCloseViewDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" component="span" fontWeight={600}>
              Requisition Details #{selectedRequisition?.id}
            </Typography>
            {selectedRequisition && (
              <StatusChip label={selectedRequisition.status} status={selectedRequisition.status} size="small" />
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedRequisition && (
            <>
              <Typography variant="subtitle2" fontWeight={600} color="primary.main" gutterBottom sx={{ mb: 2 }}>
                Request Information
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Department</Typography>
                  <Typography variant="body1" fontWeight={500}>{selectedRequisition.department}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Requested By</Typography>
                  <Typography variant="body1">{selectedRequisition.requested_by}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Item Name</Typography>
                  <Typography variant="body1" fontWeight={500}>{selectedRequisition.item_name}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Quantity Requested</Typography>
                  <Typography variant="body1">
                    {Number(selectedRequisition.quantity_requested).toLocaleString()} {selectedRequisition.unit_of_measurement || ''}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Request Date</Typography>
                  <Typography variant="body1">{selectedRequisition.request_date}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Status</Typography>
                  <StatusChip label={selectedRequisition.status} status={selectedRequisition.status} size="small" />
                </Grid>
                {selectedRequisition.notes && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Notes / Purpose</Typography>
                    <Paper variant="outlined" sx={{ p: 1.5, mt: 0.5, bgcolor: alpha(theme.palette.grey[500], 0.05) }}>
                      <Typography variant="body2">{selectedRequisition.notes}</Typography>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewDialog}>Close</Button>
          {selectedRequisition && isPendingHODApproval(selectedRequisition) && (
            <>
              <Button 
                variant="outlined" 
                color="error"
                startIcon={<DeleteOutline />}
                onClick={() => {
                  handleCloseViewDialog();
                  handleOpenRejectDialog(selectedRequisition);
                }}
              >
                Reject
              </Button>
              <Button 
                variant="contained" 
                color="success"
                startIcon={<ThumbUp />}
                onClick={() => {
                  handleCloseViewDialog();
                  handleOpenApproveDialog(selectedRequisition);
                }}
              >
                Approve
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Approve Confirmation Dialog */}
      <Dialog open={approveDialogOpen} onClose={handleCloseApproveDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <ThumbUp color="success" />
            <Typography variant="h6" component="span" fontWeight={600}>
              Approve Requisition
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Are you sure you want to approve this requisition?
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: alpha(theme.palette.grey[500], 0.05) }}>
            <Typography variant="body2">
              <strong>Item:</strong> {selectedRequisition?.item_name}<br />
              <strong>Quantity:</strong> {selectedRequisition?.quantity_requested} {selectedRequisition?.unit_of_measurement}<br />
              <strong>Requested By:</strong> {selectedRequisition?.requested_by}
            </Typography>
          </Paper>
          
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Approval Notes (Optional)"
            value={approvalNotes}
            onChange={(e) => setApprovalNotes(e.target.value)}
            placeholder="Add any remarks or conditions for this approval..."
            sx={{ mt: 2 }}
          />
          
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="caption">
              Once approved, this requisition will be forwarded to the General Manager for final approval.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseApproveDialog}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleApproveRequisition}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <ThumbUp />}
          >
            {loading ? 'Processing...' : 'Confirm Approval'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Confirmation Dialog */}
      <Dialog open={rejectDialogOpen} onClose={handleCloseRejectDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <DeleteOutline color="error" />
            <Typography variant="h6" component="span" fontWeight={600}>
              Reject Requisition
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Are you sure you want to reject this requisition?
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: alpha(theme.palette.grey[500], 0.05) }}>
            <Typography variant="body2">
              <strong>Item:</strong> {selectedRequisition?.item_name}<br />
              <strong>Quantity:</strong> {selectedRequisition?.quantity_requested} {selectedRequisition?.unit_of_measurement}<br />
              <strong>Requested By:</strong> {selectedRequisition?.requested_by}
            </Typography>
          </Paper>
          
          <TextField
            fullWidth
            required
            multiline
            rows={3}
            label="Rejection Reason"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="Please provide a reason for rejecting this requisition..."
            sx={{ mt: 2 }}
            error={!rejectionReason && rejectDialogOpen}
            helperText={!rejectionReason && rejectDialogOpen ? 'Rejection reason is required' : ''}
          />
          
          <Alert severity="error" sx={{ mt: 2 }}>
            <Typography variant="caption">
              The requisition will be marked as rejected and moved to the rejected list.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRejectDialog}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleRejectRequisition}
            disabled={loading || !rejectionReason}
            startIcon={loading ? <CircularProgress size={20} /> : <DeleteOutline />}
          >
            {loading ? 'Processing...' : 'Confirm Rejection'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        TransitionComponent={Grow}
      >
        <Alert 
          severity={snackbar.severity} 
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default HODRequisitionApproval;